import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { FAILED, IDLE, LOADING, NEEDS_REFRESH, SUCCEEDED } from "./constants";
import {
  getAllParentAccounts,
  getParentAccountTypes,
  getUnitDepositoryParentAccounts,
  saveAllParentAccounts,
} from "../../utils/api/ParentAccountsApi";

import { RootState } from "../store/store";
import AssociatedBankAccount from "../../interfaces/AssociatedBankAccount";
import { getOktaUser } from "../../utils/LocalStorage";
import {
  getAllDepositoryAssignments,
  getAllUnassignedParentAccounts,
  saveAllDepositoryAssignmentsAccounts,
} from "../../utils/api/DepositoryAssignmentsApi";

export interface depositoryAssignmentsState {
  depositoryAssignments: any[];
  unassignedParents: any[];
  unUsedBusinessUnits: any[];
  smartSafeParentAccountNumbers: any[];
  operatorNumberAndFeinList: { operatorNum: string; fein: string | null }[];
  operatorNumbers: any[];
  locationNumbers: string[];
  parentAccounts: any[];
  parentAccountTypes: any[];
  selectedUnitStatus: string;
  bulkUploadStatus: string;
  saveAllStatus: string;
  fetchAllDepositoryAssignmentsStatus: string;
  status: string;
  error: any;
  lastUpdated: string;
  unassignedBankAccountCounter: number;
}

const initialState: depositoryAssignmentsState = {
  depositoryAssignments: [],
  unUsedBusinessUnits: [],
  smartSafeParentAccountNumbers: [],
  operatorNumberAndFeinList: [],
  operatorNumbers: [],
  locationNumbers: [],
  parentAccounts: [],
  unassignedParents: [],
  parentAccountTypes: [],
  selectedUnitStatus: IDLE,
  bulkUploadStatus: IDLE,
  saveAllStatus: IDLE,
  fetchAllDepositoryAssignmentsStatus: IDLE,
  status: IDLE,
  error: null,
  lastUpdated: "",
  unassignedBankAccountCounter: 0,
};

export const fetchAllDepositoryAssignments = createAsyncThunk(
  "/depository-assignments",
  async (param, thunkAPI) => {
    let parentAccountsFetch = await getUnitDepositoryParentAccounts("ALL");
    let parentAccounts = parentAccountsFetch.items;
    let unassignedParentAccountsFetch = await getAllUnassignedParentAccounts();
    let unassignedParents =
      unassignedParentAccountsFetch.unAssignedBankAccountList;
    let locationNumbersSet: Set<string> = new Set();
    let locationNumbers: string[] = [];

    let depositoryAssignmentsFetch = await getAllDepositoryAssignments();
    let unUsedBusinessUnits: any[] = depositoryAssignmentsFetch.unUsedBUList;

    const smartSafeParentAccountNumbers: any = [];
    for (const i in parentAccounts) {
      smartSafeParentAccountNumbers.push(parentAccounts[i].bankAccountNum);
    }
    for (const i of unassignedParents) {
      smartSafeParentAccountNumbers.push(i.bankAccountNum);
    }

    const parentAccountsMap = new Map();
    for (const i of parentAccounts) {
      parentAccountsMap.set(i.id, i);
    }

    const filtered = parentAccounts.filter(
      (parentAccount: any) => parentAccount.bankAccountTypeId === 1
    );

    let depositoryAssignmentsAccounts: any = [];
    let associatedAccount: AssociatedBankAccount;

    for (const element of depositoryAssignmentsFetch.bankAccountAssocList) {
      associatedAccount = {
        ...element,
        AssociatedAccounts: Array(),
      };
      if (parentAccountsMap.get(element.parentBankAccountId) != undefined) {
        associatedAccount.parentBankAccount = parentAccountsMap.get(
          element.parentBankAccountId
        );
      } else if (associatedAccount.bankAccountTypeId === 3) {
        associatedAccount.parentBankAccount = {
          bankAccountNum: filtered[0].bankAccountNum,
        };
      } else {
        associatedAccount.parentBankAccount = {
          bankAccountNum: associatedAccount.bankAccountNum,
        };
      }
      depositoryAssignmentsAccounts.push(associatedAccount);
    }
    let unassignedBankAccountAssocInc = -1;
    for (const element of depositoryAssignmentsAccounts) {
      if (element.tempBU === null) {
        element.tempBU = false;
      }
      if (element.businessUnitNum) {
        while (element.businessUnitNum.length < 10) {
          element.businessUnitNum = "0" + element.businessUnitNum;
        }
      }
      if (element.bankAccountAssocId < 0) {
        element.bankAccountAssocId = unassignedBankAccountAssocInc;
        unassignedBankAccountAssocInc--;
      }
    }

    const operatorNumAndFein: { operatorNum: string; fein: string | null }[] =
      [];

    for (const element of unUsedBusinessUnits) {
      locationNumbersSet.add(element.businessUnitRead.locationNumber);
      operatorNumAndFein.push({
        operatorNum: element.businessUnitRead.operatorNumber,
        fein: element.businessUnitRead.fein,
      });
    }

    locationNumbers = Array.from(locationNumbersSet);
    return {
      depositoryAssignmentsAccounts,
      locationNumbers,
      operatorNumAndFein,
      unassignedParents,
      unUsedBusinessUnits,
      smartSafeParentAccountNumbers,
      parentAccounts,
      unassignedBankAccountAssocInc,
    };
  }
);

export const saveDepositoryAssignments = createAsyncThunk(
  "/save-all",
  async (saveAll: any, thunkAPI) => {
    const userName = getOktaUser()[2];
    const body: any = { userName, saveAll };

    let response;

    const importArr: any = [];
    importArr.push(saveAll);
    response = await saveAllDepositoryAssignmentsAccounts(body);
    return response;
  }
);
export const depositoryAssignmentsSlice = createSlice({
  name: "DepositoryAssignments",
  initialState,
  reducers: {
    alertRefreshDepositoryAssignments: (state) => {
      state.fetchAllDepositoryAssignmentsStatus = NEEDS_REFRESH;
    },
    resetDepositoryAssignmentsSaveAllStatus: (state) => {
      state.saveAllStatus = IDLE;
    },
    trackUnassignedBankAssocId: (state, action) => {
      state.unassignedBankAccountCounter = action.payload;
    },
    resetSaveAllStatus: (state) => {
      state.saveAllStatus = IDLE;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchAllDepositoryAssignments.pending, (state, action) => {
        state.fetchAllDepositoryAssignmentsStatus = LOADING;
      })
      .addCase(fetchAllDepositoryAssignments.fulfilled, (state, action) => {
        state.fetchAllDepositoryAssignmentsStatus = SUCCEEDED;
        state.depositoryAssignments =
          action.payload.depositoryAssignmentsAccounts;
        state.unUsedBusinessUnits = action.payload.unUsedBusinessUnits;
        state.smartSafeParentAccountNumbers =
          action.payload.smartSafeParentAccountNumbers;
        state.parentAccounts = action.payload.parentAccounts;
        state.unassignedParents = action.payload.unassignedParents;
        state.unassignedBankAccountCounter =
          action.payload.unassignedBankAccountAssocInc;
        state.locationNumbers = action.payload.locationNumbers;
        state.operatorNumberAndFeinList = action.payload.operatorNumAndFein;
      })
      .addCase(fetchAllDepositoryAssignments.rejected, (state, action) => {
        state.fetchAllDepositoryAssignmentsStatus = FAILED;
        state.error = action.error.message;
      })
      .addCase(saveDepositoryAssignments.pending, (state, action) => {
        state.saveAllStatus = LOADING;
      })
      .addCase(saveDepositoryAssignments.fulfilled, (state, action) => {
        state.saveAllStatus = SUCCEEDED;
      })
      .addCase(saveDepositoryAssignments.rejected, (state, action) => {
        state.saveAllStatus = FAILED;
        state.error = action.error.message;
      });
  },
});
export default depositoryAssignmentsSlice.reducer;
export const {
  alertRefreshDepositoryAssignments,
  resetDepositoryAssignmentsSaveAllStatus,
  trackUnassignedBankAssocId,
  resetSaveAllStatus,
} = depositoryAssignmentsSlice.actions;

export const depositoryAssignmentsAccountsSelector = (state: RootState) =>
  state.depositoryAssignments.depositoryAssignments;
export const unUsedBusSelector = (state: RootState) =>
  state.depositoryAssignments.unUsedBusinessUnits;
export const smartSafeParentAccountsNumbersForDepositoryTableSelector = (
  state: RootState
) => state.depositoryAssignments.smartSafeParentAccountNumbers;
export const smartSafeParentAccountsForDepositoryTableSelector = (
  state: RootState
) => state.depositoryAssignments.parentAccounts;
export const unUsedAccountsSelector = (state: RootState) =>
  state.depositoryAssignments.unassignedParents;
export const unassignedBankAssocIdInc = (state: RootState) =>
  state.depositoryAssignments.unassignedBankAccountCounter;
export const operatorNumbersSelector = (state: RootState) =>
  state.depositoryAssignments.operatorNumberAndFeinList;

export const locationNumbersSelector = (state: RootState) =>
  state.depositoryAssignments.locationNumbers;
